/**
 * @typedef {import('../types/plottr').Slate} Slate
 * @typedef {import('../types/plottr').SlateRootElement} SlateRootElement
 * @typedef {import('../types/plottr').TextOrLink} TextOrLink
 * @typedef {import('../types/plottr').BlockQuoteChild} BlockQuoteChild
 * @typedef {import('../types/plottr').BlockQuote} BlockQuote
 * @typedef {import('../types/plottr').Paragraph} Paragraph
 * @typedef {import('../types/plottr').HeadingOne} HeadingOne
 * @typedef {import('../types/plottr').HeadingTwo} HeadingTwo
 * @typedef {import('../types/plottr').BulletedList} BulletedList
 * @typedef {import('../types/plottr').NumberedList} NumberedList
 * @typedef {import('../types/plottr').ListChild} ListChild
 * @typedef {import('../types/plottr').ListItem} ListItem
 * @typedef {import('../types/plottr').TextChild} TextChild
 * @typedef {import('../types/plottr').SlateImageLink} SlateImageLink
 * @typedef {import('../types/plottr').SlateImageData} SlateImageData
 * @typedef {import('../types/plottr').Link} Link
 * @typedef {import('../types/system').ImageCache} ImageCache
 */

/**
 * A placeholder image to user when we haven't yet cached an image
 * required by the serialised HTML.
 *
 * @type {String}
 */
export const IMAGE_NOT_CACHED_PLACEHOLDER =
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACgAAAAoCAYAAACM/rhtAAABhGlDQ1BJQ0MgcHJvZmlsZQAAKJF9kT1Iw0AcxV9TpX5UHawg4pChOlkQFXHUKhShQqgVWnUwufQLmjQkKS6OgmvBwY/FqoOLs64OroIg+AHi6uKk6CIl/i8ptIj14Lgf7+497t4BQrXINKttHNB020zEomIqvSoGXtGNXnRiAJCZZcxJUhwtx9c9fHy9i/Cs1uf+HD1qxmKATySeZYZpE28QT2/aBud94hDLyyrxOfGYSRckfuS64vEb55zLAs8MmcnEPHGIWMw1sdLELG9qxFPEYVXTKV9Ieaxy3uKsFcusfk/+wmBGX1nmOs1hxLCIJUgQoaCMAoqwEaFVJ8VCgvajLfxDrl8il0KuAhg5FlCCBtn1g//B726t7OSElxSMAu0vjvMxAgR2gVrFcb6PHad2AvifgSu94S9VgZlP0isNLXwE9G0DF9cNTdkDLneAwSdDNmVX8tMUslng/Yy+KQ303wJda15v9X2cPgBJ6ip+AxwcAqM5yl5v8e6O5t7+PVPv7wf7enJ3iw8StgAAAAZiS0dEAP8A/wD/oL2nkwAAAAlwSFlzAAAuIwAALiMBeKU/dgAAAAd0SU1FB+cLDgkYCNOL7FwAAAAZdEVYdENvbW1lbnQAQ3JlYXRlZCB3aXRoIEdJTVBXgQ4XAAAARklEQVRYw+3YIQ4AIAwEwSvh/18GiwZTkllVOTnZSrLSuJHmzeOuZrb1xYKAgICAgICAgICAgICAgICAgICAgIC3VXz539rLOwNPnv65RAAAAABJRU5ErkJggg=='

// From: https://github.com/tbluemel/rtf.js/blob/HEAD/GETTING_STARTED.md
function stringToArrayBuffer(string) {
  const buffer = new ArrayBuffer(string.length)
  const bufferView = new Uint8Array(buffer)
  for (let i = 0; i < string.length; i++) {
    bufferView[i] = string.charCodeAt(i)
  }
  return buffer
}

function stripListTable(rtfString) {
  const startListTable = rtfString.indexOf('{\\*\\listtable')
  if (startListTable === -1) {
    return rtfString
  } else {
    // 12 is the length of the string that starts the list table.
    let endListTable = startListTable + 12
    for (let depth = 1; endListTable < rtfString.length && depth > 0; ++endListTable) {
      if (rtfString[endListTable] === '}') {
        --depth
      } else if (rtfString[endListTable] === '{') {
        ++depth
      }
    }
    return rtfString.slice(0, startListTable) + rtfString.slice(endListTable)
  }
}

// String -> Promise<NodeList>
export const rtfToHTML = (string) => {
  return import('rtf.js').then(({ RTFJS }) => {
    RTFJS.loggingEnabled(false)

    // @ts-ignore
    const doc = new RTFJS.Document(stringToArrayBuffer(stripListTable(string)))
    return doc.render().catch((error) => {
      // FIXME: this wont work on the web :/
      console.error('rtfjs', error)
      return []
    })
  })
}

/**
 * @typedef MightHaveID
 * @property {String} [id]
 */

/**
 * Produce a string with an HTML data attribute mapping to the slate
 * node's id if the slate node has one.  Otherwise, produce the empty
 * string.
 *
 * N.B. Don't pad the callsight with whitespace.  This function adds
 * whitespace if there was an id to create a data attribute from, and
 * otherwise adds a single whitespace.
 *
 * @param {MightHaveID} node
 * @return {String}
 */
function idDataAttribute(node) {
  if (node.id) {
    return ` data-id="${node.id}" `
  } else {
    return ' '
  }
}

/**
 * @param {TextChild} text
 * @return {String}
 */
export function serialiseSlateText(text) {
  const styles = [
    text.bold ? ['font-weight', 'bold'] : [],
    text.italic ? ['font-style', 'italic'] : [],
    text.underline ? ['text-decoration', 'underline'] : [],
    text.strike ? ['text-decoration', 'line-through'] : [],
    text.color ? ['color', text.color] : [],
    text.font ? ['font-family', text.font] : [],
    text.fontSize ? ['font-size', text.fontSize] : [],
  ]
    .flatMap((pair) => {
      if (pair.length) {
        return [pair.join(':')]
      } else {
        return []
      }
    })
    .join(';')

  return `<span${idDataAttribute(text)}${styles.length ? ` style="${styles}"` : ''}>${
    text.text
  }</span>`
}

/**
 * @param {Link} link
 * @return {String}
 */
export function serialiseLink(link) {
  return `<a${idDataAttribute(link)}href="${link.url}">${link.children
    .map(serialiseSlateText)
    .join('')}</a>`
}

/**
 * @param {TextOrLink} paragraphChild
 * @return {String}
 */
export function serialiseParagraphChild(paragraphChild) {
  if ('text' in paragraphChild) {
    return serialiseSlateText(paragraphChild)
  } else if ('type' in paragraphChild && paragraphChild.type === 'link') {
    return serialiseLink(paragraphChild)
  } else {
    throw new Error('Invariant violated.  Invalid paragraph child.')
  }
}

/**
 * @param {Paragraph} paragraph
 * @return {String}
 */
export function serialiseParagraph(paragraph) {
  return `<p${idDataAttribute(paragraph)}>${paragraph.children
    .map(serialiseParagraphChild)
    .join('')}</p>`
}

/**
 * @param {HeadingOne} headingOne
 * @return {String}
 */
export function serialiseHeadingOne(headingOne) {
  return `<h1${idDataAttribute(headingOne)}>${headingOne.children
    .map(serialiseSlateText)
    .join('')}</h1>`
}

/**
 * @param {HeadingTwo} headingTwo
 * @return {String}
 */
export function serialiseHeadingTwo(headingTwo) {
  return `<h2${idDataAttribute(headingTwo)}>${headingTwo.children
    .map(serialiseSlateText)
    .join('')}</h2>`
}

/**
 * @param {ListItem} listItem
 * @return {String}
 */
export function serialiseListItem(listItem) {
  return `<li${idDataAttribute(listItem)}>${listItem.children
    .map(serialiseSlateText)
    .join('')}</li>`
}

/**
 * @param {NumberedList} numberedList
 * @return {String}
 */
export function serialiseNumberedList(numberedList) {
  return `<ol${idDataAttribute(numberedList)}>${numberedList.children
    .map(serialiseListChild)
    .join('')}</ol>`
}

/**
 * @param {ListChild} listChild
 * @return {String}
 */
export function serialiseListChild(listChild) {
  switch (listChild.type) {
    case 'list-item': {
      return serialiseListItem(listChild)
    }
    case 'bulleted-list': {
      return serialiseBulletedList(listChild)
    }
    case 'numbered-list': {
      return serialiseNumberedList(listChild)
    }
    default: {
      // This should never happen and is here to silence eslint
      throw new Error('Invalid list child type')
    }
  }
}

/**
 * @param {BulletedList} bulletedList
 * @return {String}
 */
export function serialiseBulletedList(bulletedList) {
  return `<ul${idDataAttribute(bulletedList)}>${bulletedList.children
    .map(serialiseListChild)
    .join('')}</ul>`
}

/**
 * @param {BlockQuoteChild} blockQuoteChild
 * @return {String}
 */
export function serialiseBlockQuoteChild(blockQuoteChild) {
  switch (blockQuoteChild.type) {
    case 'paragraph': {
      return serialiseParagraph(blockQuoteChild)
    }
    case 'heading-one': {
      return serialiseHeadingOne(blockQuoteChild)
    }
    case 'heading-two': {
      return serialiseHeadingTwo(blockQuoteChild)
    }
    case 'bulleted-list': {
      return serialiseBulletedList(blockQuoteChild)
    }
    default: {
      // This should never happen and is here to silence eslint
      throw new Error('Invalid block quote child type')
    }
  }
}

/**
 * @param {BlockQuote} blockQuote
 * @return String
 */
export function serialiseBlockQuote(blockQuote) {
  return `<blockquote${idDataAttribute(blockQuote)}>${blockQuote.children
    .map(serialiseBlockQuoteChild)
    .join('')}</blockquote>`
}

/**
 * @param {SlateImageLink} imageLink
 * @param {ImageCache} imageCache
 * @return {String}
 */
export function serialiseImageLink(imageLink, imageCache) {
  const publicURL = imageCache[imageLink.storageUrl]
  if (publicURL) {
    return `<img${idDataAttribute(imageLink)}src="${publicURL}" data-storageURL=${
      imageLink.storageUrl
    } />`
  } else {
    return `<img${idDataAttribute(imageLink)}src="${IMAGE_NOT_CACHED_PLACEHOLDER}" />`
  }
}

/**
 * @param {SlateImageData} imageData
 * @return {String}
 */
export function serialiseImageData(imageData) {
  return `<img${idDataAttribute(imageData)}src="${imageData.data}" />`
}

/**
 * @param {SlateRootElement} element
 * @param {ImageCache} imageCache
 * @return {String}
 */
export function serialiseAnyRootSlateElement(element, imageCache) {
  switch (element.type) {
    case 'paragraph': {
      return serialiseParagraph(element)
    }
    case 'block-quote': {
      return serialiseBlockQuote(element)
    }
    case 'heading-one': {
      return serialiseHeadingOne(element)
    }
    case 'heading-two': {
      return serialiseHeadingTwo(element)
    }
    case 'bulleted-list': {
      return serialiseBulletedList(element)
    }
    case 'numbered-list': {
      return serialiseNumberedList(element)
    }
    case 'image-link': {
      return serialiseImageLink(element, imageCache)
    }
    case 'image-data': {
      return serialiseImageData(element)
    }
    default: {
      throw new Error('Invariant violated: invalid slate element')
    }
  }
}

/**
 * Convert the supplied slate into HTML that can be converted back
 * cleanly.  On the return trip, look for data attributes that hint at
 * the location of images that are already stored in Pro storage so we
 * don't upload them twice.
 *
 * @param {Slate} slate
 * @param {ImageCache} imageCache
 * @return {String}
 */
export function slateToHTML(slate, imageCache) {
  return slate.reduce((stringAcc, nextNode) => {
    const serialisedNode = serialiseAnyRootSlateElement(nextNode, imageCache)
    return stringAcc + serialisedNode
  }, '')
}
